import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ReviewStatusFilter = "pending" | "approved" | "needs_revision" | "all";
export type RecencyFilter = "all" | "24h" | "7d";
export type ReviewSort = "oldest_pending" | "ai_risk" | "newest";

export interface ReviewMcq {
  id: string;
  stem: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string | null;
  option_e: string | null;
  correct_option: string;
  explanation: string | null;
  difficulty: number | null;
  tags: string[] | null;
  review_status: string | null;
  topic_id: string | null;
  created_at: string;
  topics?: { id: string; name: string } | null;
  /** Hydrated lazily from `ai_coach_cache` review-panel entries. */
  ai_risk?: { verdict: string; confidence: number } | null;
}

export interface ReviewFilters {
  status: ReviewStatusFilter;
  examLabelId: string | null;
  topicId: string | null;
  recency: RecencyFilter;
  /** Only show MCQs with cached AI verification flagged as risky. */
  aiRiskOnly: boolean;
}

export const DEFAULT_FILTERS: ReviewFilters = {
  status: "pending",
  examLabelId: null,
  topicId: null,
  recency: "all",
  aiRiskOnly: false,
};

const sb = supabase as any;

function recencyCutoff(r: RecencyFilter): string | null {
  if (r === "24h") return new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  if (r === "7d") return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  return null;
}

/**
 * Loads the review queue with filters + sort and exposes a cursor that points
 * at the currently selected MCQ. Provides advance helpers used by both the
 * platform page and the focus reviewer.
 */
export function useReviewQueue(initialFilters: Partial<ReviewFilters> = {}) {
  const [filters, setFilters] = useState<ReviewFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });
  const [sort, setSort] = useState<ReviewSort>("oldest_pending");
  const [items, setItems] = useState<ReviewMcq[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Optional MCQ id filter from exam label.
      let mcqIdFilter: string[] | null = null;
      if (filters.examLabelId) {
        const { data: linkRows, error: linkErr } = await sb
          .from("mcq_exam_labels")
          .select("mcq_id")
          .eq("label_id", filters.examLabelId)
          .limit(2000);
        if (linkErr) throw linkErr;
        mcqIdFilter = (linkRows || []).map((r: any) => r.mcq_id);
        if (mcqIdFilter.length === 0) {
          setItems([]);
          setLoading(false);
          return;
        }
      }

      // 2. Build MCQ query.
      let q = sb
        .from("mcqs")
        .select("*, topics:topic_id(id, name)")
        .limit(150);

      if (filters.status !== "all") q = q.eq("review_status", filters.status);
      if (filters.topicId) q = q.eq("topic_id", filters.topicId);
      const cutoff = recencyCutoff(filters.recency);
      if (cutoff) q = q.gte("created_at", cutoff);
      if (mcqIdFilter) q = q.in("id", mcqIdFilter);

      // Server sort for non-AI sorts.
      if (sort === "newest") q = q.order("created_at", { ascending: false });
      else q = q.order("created_at", { ascending: true });

      const { data, error: mcqErr } = await q;
      if (mcqErr) throw mcqErr;

      let rows: ReviewMcq[] = (data || []) as ReviewMcq[];

      // 3. Hydrate AI risk pills (best-effort).
      if (rows.length > 0) {
        const ids = rows.map((r) => r.id);
        const { data: cacheRows } = await sb
          .from("ai_coach_cache")
          .select("mcq_id, response_content, created_at")
          .in("mcq_id", ids)
          .like("cache_key", "review-panel:%");
        const riskByMcq = new Map<string, { verdict: string; confidence: number }>();
        for (const c of cacheRows || []) {
          try {
            const parsed = JSON.parse(c.response_content);
            const verdict = parsed?.consensus?.verdict;
            const confidence = parsed?.consensus?.confidence;
            if (verdict && typeof confidence === "number") {
              const prev = riskByMcq.get(c.mcq_id);
              // Keep latest by created_at if multiple.
              if (!prev) riskByMcq.set(c.mcq_id, { verdict, confidence });
            }
          } catch {
            /* ignore */
          }
        }
        rows = rows.map((r) => ({
          ...r,
          ai_risk: riskByMcq.get(r.id) || null,
        }));
      }

      // 4. Apply AI-risk-only filter client-side.
      if (filters.aiRiskOnly) {
        rows = rows.filter(
          (r) =>
            r.ai_risk &&
            (r.ai_risk.verdict !== "approve" || r.ai_risk.confidence < 70)
        );
      }

      // 5. Sort by AI risk (lowest confidence first; un-reviewed at the end).
      if (sort === "ai_risk") {
        rows.sort((a, b) => {
          const ra = a.ai_risk?.confidence ?? 1000;
          const rb = b.ai_risk?.confidence ?? 1000;
          return ra - rb;
        });
      }

      setItems(rows);
      // Auto-select first if nothing selected or selection no longer in list.
      setSelectedId((cur) => {
        if (cur && rows.some((r) => r.id === cur)) return cur;
        return rows[0]?.id ?? null;
      });
    } catch (e: any) {
      setError(e?.message || "Failed to load review queue");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters, sort]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => items.find((m) => m.id === selectedId) || null,
    [items, selectedId]
  );
  const currentIndex = useMemo(
    () => (selectedId ? items.findIndex((m) => m.id === selectedId) : -1),
    [items, selectedId]
  );

  const selectByIndex = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= items.length) return;
      setSelectedId(items[idx].id);
    },
    [items]
  );

  const next = useCallback(() => {
    if (currentIndex < 0) return;
    if (currentIndex + 1 < items.length) selectByIndex(currentIndex + 1);
  }, [currentIndex, items.length, selectByIndex]);

  const prev = useCallback(() => {
    if (currentIndex < 0) return;
    if (currentIndex - 1 >= 0) selectByIndex(currentIndex - 1);
  }, [currentIndex, selectByIndex]);

  /** Optimistically remove an MCQ and advance to the next one. */
  const removeAndAdvance = useCallback(
    (id: string) => {
      setItems((cur) => {
        const idx = cur.findIndex((m) => m.id === id);
        if (idx < 0) return cur;
        const nextItems = [...cur.slice(0, idx), ...cur.slice(idx + 1)];
        const nextSel = nextItems[idx] ?? nextItems[idx - 1] ?? null;
        setSelectedId(nextSel?.id ?? null);
        return nextItems;
      });
    },
    []
  );

  return {
    filters,
    setFilters,
    sort,
    setSort,
    items,
    loading,
    error,
    selected,
    selectedId,
    setSelectedId,
    currentIndex,
    next,
    prev,
    removeAndAdvance,
    reload: load,
  };
}
